/* eslint-disable react/no-unused-prop-types */
// Have to disable for styling overrides
import React, { FC, useState, useLayoutEffect } from 'react';
import injectSheet from 'react-jss';
import { DateRangePicker } from 'react-dates';
import isBeforeDay from 'react-dates/lib/utils/isBeforeDay';
import isAfterDay from 'react-dates/lib/utils/isAfterDay';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import moment from 'moment';
import Portal from 'HOC/Portal/Portal';
// eslint-disable-next-line import/no-extraneous-dependencies
import { v4 } from 'uuid';
import { inRange } from './utils';
import styleSheet from './styles';

interface Props {
  classes?: any;
  startDate: string;
  endDate: string;
  minDate?: string;
  maxDate?: string;

  // All for styling
  pickerColor?: string;
  pickerBorderWidth?: string;
  bottomRight?: string;
  selectedBackgroundColor?: string;
  selectedColor?: string;
  selectedSpanBackgroundColor?: string;
  selectedSpanColor?: string;
  inputArrowColor?: string;
  dateInputColor?: string;

  cutOffTop?: number;
  usePortals?: boolean;
  closeOnScroll?: boolean;
  anchorDirection?: 'right' | 'left';

  onChange?: (direction: string, date: string) => void;
}

interface DateChanges {
  startDate: string;
  endDate: string;
}

const isValidDate = (date: string) =>
  moment(date, 'MM-DD-YYYY', true).isValid();
const defaultMoments = (
  startDate: string,
  endDate: string,
  minDate: string,
  maxDate: string
) => {
  // Return all the dates as moment objs
  return {
    startDateMoment:
      startDate === null || startDate === undefined || startDate === ''
        ? moment() // Default to yesterday as a start date if one is not passed in
            .subtract(1, 'day')
        : moment(startDate, 'MM-DD-YYYY'),
    endDateMoment:
      endDate === null || endDate === undefined || endDate === ''
        ? moment() // Default to today if an endDate is not passed in
        : moment(endDate, 'MM-DD-YYYY'),
    minDateMoment:
      minDate === null || minDate === undefined || minDate === ''
        ? moment().subtract(1, 'month') // Default to last month
        : moment(minDate, 'MM-DD-YYYY'),
    maxDateMoment:
      maxDate === null || maxDate === undefined || maxDate === ''
        ? moment() // Default to last month
        : moment(maxDate, 'MM-DD-YYYY'),
  };
};
const RangeDatePicker: FC<Props> = (props: Props) => {
  const {
    classes,
    startDate,
    endDate,
    minDate,
    maxDate,
    onChange,
    cutOffTop,
    usePortals,
    closeOnScroll,
    anchorDirection,
  } = props;

  // Set Defaults
  const moments = defaultMoments(startDate, endDate, minDate, maxDate);

  const [focusInput, setFocusInput] = useState(null);
  const [minDateMoment] = useState(moments.minDateMoment);
  const [maxDateMoment] = useState(moments.maxDateMoment);
  const [scrolling, setScrolling] = useState(false);
  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);
  const [id] = useState(v4());

  const onClose = ({
    startDate: onCloseStart,
    endDate: onCloseEnd,
  }: DateChanges) => {
    // Convert the dates to the format that we will use in our app
    const startResponse = moment(onCloseStart, 'MM/DD/YYYY').format(
      'MM/DD/YYYY'
    );
    const closeResponse = moment(onCloseEnd, 'MM/DD/YYYY').format('MM/DD/YYYY');

    // Check if they are the same as the startDate and endDates
    if (
      startResponse === moments.startDateMoment.format('MM/DD/YYYY') &&
      closeResponse === moments.endDateMoment.format('MM/DD/YYYY')
    )
      return; // Same dates no need for an update.

    // Looks like at least one date was different, perform and update
    onChange(startResponse, closeResponse);
  };

  const onDatesChange = ({ startDate: start, endDate: end }: DateChanges) => {
    /*
        Anything resembling a date, the calendar will pass in nulls.  When it finally gets a valid dates
        It will pass the start and end in.  In order to avoid a lot of updates, we just return if 
        start and end are null
      */

    const startDateFormatted = moments.startDateMoment.format('MM-DD-YYYY');
    const endDateFormatted = moments.endDateMoment.format('MM-DD-YYYY');
    if (start === null && end === null) return;
    let startStatus = start === null;
    let endStatus = end === null;
    let startResponse = start;
    let endResponse = end;

    // AirBnb - bug fix
    if (!startStatus && endStatus) {
      // This is to fix a bug.  end is null, set it to equal start until airbnb fixes this bug
      endResponse = startResponse;
      // Need to reset the status
      endStatus = false;
    }
    // AirBnb - bug fix

    // Normalize the dates and get rid of the nulls
    startResponse = startStatus
      ? startDateFormatted
      : moment(startResponse).format('MM-DD-YYYY');
    endResponse = endStatus
      ? endDateFormatted
      : moment(endResponse).format('MM-DD-YYYY');

    // Are the dates the same as the stored dates
    startStatus = startResponse === startDateFormatted;
    endStatus = endResponse === endDateFormatted;

    // If both are true, then return, we do not want to trigger an update with the same dates.
    if (startStatus && endStatus) return;

    // Are the dates valid
    startStatus = isValidDate(startResponse);
    endStatus = isValidDate(endResponse);

    // reset to the starDate if the startStatus is false
    startResponse = !startStatus ? startDateFormatted : startResponse;
    endResponse = !endStatus ? endDateFormatted : endResponse;

    // Check if start and end are outside the minDate / MaxDate date Range
    startStatus = inRange(
      moment(startResponse, 'MM-DD-YYYY'),
      moments.minDateMoment,
      moments.maxDateMoment
    );
    endStatus = inRange(
      moment(endResponse, 'MM-DD-YYYY'),
      moments.minDateMoment,
      moments.maxDateMoment
    );

    // if in range, go with the start, otherwise, default to the last known valid start and/or end date
    startResponse = startStatus ? startResponse : startDateFormatted;
    endResponse = endStatus ? endResponse : endDateFormatted;

    if (startStatus || endStatus) {
      // If one are true, then perform an update.  Other wise they are the same as the values in dates and no need
      // to update

      onChange(
        // Send it back in the format our app uses
        moment(startResponse, 'MM/DD/YYYY').format('MM/DD/YYYY'),
        moment(endResponse, 'MM/DD/YYYY').format('MM/DD/YYYY')
      );
    }
  };

  const getTopAndLeft = () => {
    const element = document.getElementById(id);
    let offSetLeft = 0;
    let offSetTop = 0;

    if (element) {
      offSetTop = element.getBoundingClientRect().top + window.scrollY;
      offSetLeft = element.getBoundingClientRect().left + window.scrollX;
    }

    if (offSetTop !== top) {
      setTop(offSetTop);
    }

    if (offSetLeft !== left) {
      setLeft(offSetLeft);
    }
  };

  useLayoutEffect(() => {
    getTopAndLeft();
  }, [focusInput, scrolling]);

  useLayoutEffect(() => {
    let timer;

    window.addEventListener(
      'scroll',
      () => {
        if (closeOnScroll) {
          setFocusInput(null);
        }

        if (timer !== null) {
          setScrolling(true);
          clearTimeout(timer);
        }

        timer = setTimeout(() => {
          setScrolling(false);
        }, 150);
      },
      true
    );
  }, []);

  const showPortal = () => {
    let response = false;

    if (!scrolling && focusInput && usePortals) {
      if (typeof cutOffTop === 'number') {
        if (top >= cutOffTop) {
          response = true;
        } else {
          response = false;
        }
      } else {
        response = true;
      }
    }

    return response;
  };

  return (
    <div style={{ height: '35px' }} id={id}>
      {(showPortal() && (
        <Portal>
          <div
            className={classes.datePickerContainer}
            style={{
              position: 'fixed',
              zIndex: 27,
              top: `${top}px`,
              left: `${left}px`,
            }}
          >
            <DateRangePicker
              keepOpenOnDateSelect
              small // Styling for a small calendar
              isOutsideRange={(day: any) => {
                // This works with minDate, limiting how far back a user can go
                return (
                  isAfterDay(day, moment().subtract(1, 'days')) ||
                  isBeforeDay(day, moment(minDate, 'MM/DD/YYYY'))
                );
              }}
              // This limits how far how far back a user can go
              minDate={minDateMoment}
              // This limits how far ahead they can go in the future
              maxDate={maxDateMoment}
              isDayHighlighted={() => false}
              startDate={moments.startDateMoment}
              startDateId="start" // PropTypes.string.isRequired,
              endDate={moments.endDateMoment} // momentPropTypes.momentObj or null,
              endDateId="end" // PropTypes.string.isRequired,
              onClose={onClose}
              onDatesChange={onDatesChange} // PropTypes.func.isRequired,
              focusedInput={focusInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
              onFocusChange={
                (focusedInput: any) => {
                  setFocusInput(focusedInput);
                } // PropTypes.func.isRequired,
              }
              numberOfMonths={3}
              anchorDirection={anchorDirection}
            />
          </div>
        </Portal>
      )) || (
        <div
          className={classes.datePickerContainer}
          style={{
            pointerEvents:
              (typeof cutOffTop === 'number' &&
                top > 0 &&
                top <= cutOffTop &&
                'none') ||
              'auto',
          }}
        >
          <DateRangePicker
            keepOpenOnDateSelect
            small // Styling for a small calendar
            isOutsideRange={(day: any) => {
              // This works with minDate, limiting how far back a user can go
              return (
                isAfterDay(day, moment().subtract(1, 'days')) ||
                isBeforeDay(day, moment(minDate, 'MM/DD/YYYY'))
              );
            }}
            // This limits how far how far back a user can go
            minDate={minDateMoment}
            // This limits how far ahead they can go in the future
            maxDate={maxDateMoment}
            isDayHighlighted={() => false}
            startDate={moments.startDateMoment}
            startDateId="start" // PropTypes.string.isRequired,
            endDate={moments.endDateMoment} // momentPropTypes.momentObj or null,
            endDateId="end" // PropTypes.string.isRequired,
            onClose={onClose}
            onDatesChange={onDatesChange} // PropTypes.func.isRequired,
            focusedInput={focusInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
            onFocusChange={
              (focusedInput: any) => {
                setFocusInput(focusedInput);
              } // PropTypes.func.isRequired,
            }
            numberOfMonths={3}
            anchorDirection={anchorDirection}
          />
        </div>
      )}
    </div>
  );
};

RangeDatePicker.defaultProps = {
  classes: {},
  minDate: undefined,
  maxDate: undefined,
  pickerColor: undefined,
  pickerBorderWidth: undefined,
  bottomRight: undefined,
  selectedBackgroundColor: undefined,
  selectedColor: undefined,
  selectedSpanBackgroundColor: undefined,
  selectedSpanColor: undefined,
  inputArrowColor: undefined,
  dateInputColor: undefined,
  cutOffTop: undefined,
  usePortals: false,
  closeOnScroll: false,
  anchorDirection: 'left',
  onChange: () => {},
};

export default injectSheet(styleSheet)(RangeDatePicker);
